import { File as DenthubFile } from './file';
import { Order, RestorationType } from './order';
import { Organization } from './organization';

export type ScannerCaseAuthStatus = {
    enabled: boolean;
    authorized: boolean;
    clientId: string;
    codeChallenge?: string;
    state?: string;
    clientSecret?: string;
};

export type ScannerCase = {
    _id: string;
    caseId: string;
    displayCaseId: string;
    version: number;
    source: ScanSource;
    orderStatus: ScannerCaseOrderStatus;
    externalSourceStatus?: string;
    patient: {
        firstName: string;
        lastName: string;
        patientRefNo: string;
    };
    dentist: {
        name: string;
        externalId: string;
        email?: string;
    };
    clinic: {
        name: string;
        externalId: string;
    };
    comments?: string;
    createdAt: Date;
    updatedAt: Date;
    organization: Organization;
    rawCaseData: any;
    sourceFiles: ScannerCaseSourceFile[];
    deliveryDate?: Date;
    restorations: ScannerCaseRestoration[];
    // DD-3445: distinct constructions, derived by the backend from `restorations`.
    constructionTypes?: ScannerCaseConstructionType[];
    fileProcessingStatus: ScannerFileProcessingStatus;
    processedFiles: DenthubFile[];
    previewImages: DenthubFile[];
    printableOrderForm?: PrintableOrderForm;
    orders: Order[];
};

export type PrintableOrderForm = {
    file: DenthubFile;
    type: PrintableOrderFormFileType;
};

export enum PrintableOrderFormFileType {
    HTML = 'HTML',
    PDF = 'PDF',
}

export enum ScannerCaseOrderStatus {
    NEW = 'NEW',
    ORDER_CREATED = 'ORDER_CREATED',
    DO_NOT_IMPORT = 'DO_NOT_IMPORT',
}

export enum CaseListSourceStatus {
    NOT_AUTHENTICATED = 'NOT_AUTHENTICATED',
    IN_SYNC = 'IN_SYNC',
    PERFORMING_SYNC = 'PERFORMING_SYNC',
    ERROR = 'ERROR',
    LOCKED = 'LOCKED',
}

export type CaseListSourceStatuses = {
    threeShape?: CaseListStatus;
    medit?: CaseListStatus;
    dexis?: CaseListStatus;
    panda?: CaseListStatus;
    pandaEu?: CaseListStatus;
    dsCore?: CaseListStatus;
};

export type CaseListStatus = {
    status: CaseListSourceStatus;
    statusUpdated: Date;
};

export type ScannerCaseFilter = {
    search?: string;
    orderStatus?: ScannerCaseOrderStatus;
    source?: ScanSource;
};

// Scanner Inbox order on "Last updated" (DD-3548).
export enum ScannerCaseSortOrder {
    NEWEST_FIRST = 'NEWEST_FIRST',
    OLDEST_FIRST = 'OLDEST_FIRST',
}

export type ScannerCaseSourceFile = {
    fileSourceId: string;
    fileName: string;
    type: ScannerCaseFileType;
    disallowDownload?: boolean;
};

export enum ScannerCaseFileType {
    SCAN = 'SCAN',
    IMAGE = 'IMAGE',
    OTHER = 'OTHER',
}

export type ScannerCaseRestoration = {
    teethNumbers?: number[];
    jaw?: 'UPPER_JAW' | 'LOWER_JAW';
    product?: string;
    material?: string;
    shade?: string;
    iconUrl?: string;
    constructionType?: string;
    restorationType?: RestorationType;
};

// `restorationType` is null when the scanner's value has no Denthub mapping yet;
// `label` is always the scanner's own readable name.
export type ScannerCaseConstructionType = {
    restorationType?: RestorationType | null;
    label: string;
};

export enum ScannerFileProcessingStatus {
    NOT_STARTED = 'NOT_STARTED',
    PROCESSING = 'PROCESSING',
    COMPLETED = 'COMPLETED',
    ERROR = 'ERROR',
}

export enum ScanSource {
    THREE_SHAPE = 'THREE_SHAPE',
    MEDIT = 'MEDIT',
    DEXIS = 'DEXIS',
    PANDA = 'PANDA',
    PANDA_EU = 'PANDA_EU',
    DS_CORE = 'DS_CORE',
}
